import React, { useState, useRef, useEffect } from "react";
import "./DesignEditor.css";

const PencilIcon = ({ active }) => (
  <svg
    width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke={active ? "#09F984" : "#38bdf8"}
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
  >
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
  </svg>
);

// rgb(a) → #rrggbb
const rgbToHex = (rgb) => {
  const m = rgb.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!m) return null;
  return "#" + [m[1], m[2], m[3]]
    .map((n) => (+n).toString(16).padStart(2, "0"))
    .join("");
};

const isTransparent = (color) => {
  if (!color || color === "transparent") return true;
  // rgba(x,x,x,0)
  const m = color.match(/rgba\([^)]+,\s*([\d.]+)\)/);
  if (m && parseFloat(m[1]) === 0) return true;
  return false;
};

// DOM ağacını yukarı tara — ilk opak arka planı bul
const pickColorFromElement = (el) => {
  let cur = el;
  while (cur && cur !== document.documentElement) {
    const bg = getComputedStyle(cur).backgroundColor;
    if (!isTransparent(bg)) return rgbToHex(bg);
    cur = cur.parentElement;
  }
  return null;
};

const toHex6 = (value) => {
  if (!value) return '#000000';
  if (value.startsWith('#') && value.length >= 7) return value.slice(0, 7);
  return '#000000';
};

const ColorRow = ({ label, colorKey, value, alphaValue, isPickActive, onPencilClick, onColorChange, onReset }) => {
  const alphaKey = `${colorKey}Alpha`;
  // Alpha 0-100 görünür (yüzde). null/undefined → "Otomatik" (CSS default kullanılır)
  const alphaDisplay = alphaValue === null || alphaValue === undefined
    ? null
    : Math.round(alphaValue * 100);

  return (
    <div className="color-input">
      <div className="color-row-main">
        <label>{label}</label>
        <button
          className={`color-edit-btn${isPickActive ? " active" : ""}`}
          onClick={() => onPencilClick(colorKey)}
          title={isPickActive ? "İptal (Esc)" : "Sayfadan renk seç"}
        >
          <PencilIcon active={isPickActive} />
        </button>
        <label className="color-swatch-container" style={{ cursor: 'pointer' }}>
          <div className="color-swatch" style={{ backgroundColor: value }} />
          <input
            type="color"
            value={toHex6(value)}
            onChange={(e) => onColorChange(colorKey, e.target.value)}
            style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
          />
        </label>
        <span className="color-value-text">{value}</span>
        <button
          className="color-reset-btn"
          onClick={() => onReset(colorKey)}
          title="Varsayılana sıfırla"
          aria-label="Sıfırla"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="1 4 1 10 7 10" />
            <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
          </svg>
        </button>
      </div>
      <div className="color-alpha-row">
        <span className="alpha-label">α</span>
        <input
          type="range"
          min="0"
          max="100"
          step="1"
          value={alphaDisplay !== null ? alphaDisplay : 100}
          onChange={(e) => onColorChange(alphaKey, parseInt(e.target.value, 10) / 100)}
          className="alpha-slider"
        />
        <span className="alpha-value">
          {alphaDisplay !== null ? `${alphaDisplay}%` : 'Otomatik'}
        </span>
      </div>
    </div>
  );
};

const fontOptions = [
  // Modern & Clean
  { label: "Poppins (Varsayılan Buton)", value: "Poppins" },
  { label: "Montserrat (Varsayılan Çark)", value: "Montserrat" },
  { label: "Inter", value: "Inter" },
  { label: "Outfit", value: "Outfit" },
  { label: "Plus Jakarta Sans", value: "Plus Jakarta Sans" },
  { label: "Manrope", value: "Manrope" },
  { label: "Urbanist (Premium Modern)", value: "Urbanist" },
  { label: "Space Grotesk (Tech Modern)", value: "Space Grotesk" },
  { label: "Sora (Geometrik Premium)", value: "Sora" },
  { label: "Jost (Minimalist)", value: "Jost" },
  { label: "Lexend (Temiz)", value: "Lexend" },
  { label: "DM Sans (Modern Geometrik)", value: "DM Sans" },
  { label: "Rubik", value: "Rubik" },
  { label: "Roboto", value: "Roboto" },
  { label: "Kanit", value: "Kanit" },

  // Elegant & Luxury (Serif & Chic)
  { label: "Cormorant Garamond (Lüks & Asil)", value: "Cormorant Garamond" },
  { label: "Cinzel Decorative (Kraliyet & Altın)", value: "Cinzel Decorative" },
  { label: "Bodoni Moda (Yüksek Moda)", value: "Bodoni Moda" },
  { label: "Instrument Serif (Zarif İnce)", value: "Instrument Serif" },
  { label: "Playfair Display (Premium Klasik)", value: "Playfair Display" },
  { label: "Prata (Didone Tarzı Zarif)", value: "Prata" },
  { label: "Tenor Sans (Modern Şık)", value: "Tenor Sans" },
  { label: "Lora (Zarif)", value: "Lora" },
  { label: "Merriweather (Klasik)", value: "Merriweather" },
  { label: "Georgia (Geleneksel)", value: "Georgia" },

  // Bold & Gaming & Display (Kalın & Dikkat Çekici)
  { label: "Unbounded (Geniş Oyuncu)", value: "Unbounded" },
  { label: "Righteous (Art Deco / Retro)", value: "Righteous" },
  { label: "Bebas Neue (Uzun & Temiz)", value: "Bebas Neue" },
  { label: "Syne (Sıradışı Modern)", value: "Syne" },
  { label: "Cinzel (Klasik Roma)", value: "Cinzel" },
  { label: "Bricolage Grotesque (Dinamik)", value: "Bricolage Grotesque" },
  { label: "Anton (Devasa & Kalın)", value: "Anton" },
  { label: "Lilita One (Tombul & Eğlenceli)", value: "Lilita One" },
  { label: "Russo One (Fütüristik Oyuncu)", value: "Russo One" },
  { label: "Archivo Black (Ekstra Kalın)", value: "Archivo Black" },
  { label: "Oswald (Modern Sıkışık)", value: "Oswald" },
  { label: "Impact (Klasik Kalın)", value: "Impact" },
  { label: "Arial Black (Sistem Kalın)", value: "Arial Black" }
];

const DesignEditor = ({
  colors, onColorChange, onClose,
  customBgImage, onBgImageUpload, onBgImageRemove,
  customLogoImage, onLogoImageUpload, onLogoImageRemove,
}) => {
  const [pickMode, setPickMode] = useState({ active: false, key: null });
  const [bgUploading, setBgUploading] = useState(false);
  const bgFileInputRef = useRef(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoFileInputRef = useRef(null);
  // Faz C — tab + search state
  const [activeTab, setActiveTab] = useState('images');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({
    x: window.innerWidth - 420,
    y: Math.max(20, (window.innerHeight - 700) / 2),
  });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const editorRef = useRef(null);
  const onColorChangeRef = useRef(onColorChange);
  useEffect(() => { onColorChangeRef.current = onColorChange; }, [onColorChange]);

  const handlePencilClick = async (key) => {
    // Aynı kaleme tekrar basılırsa iptal
    if (pickMode.active && pickMode.key === key) {
      setPickMode({ active: false, key: null });
      return;
    }

    // --- EyeDropper API (Chrome / Edge 95+) ---
    if (typeof window.EyeDropper !== "undefined") {
      try {
        const dropper = new window.EyeDropper();
        const { sRGBHex } = await dropper.open();
        onColorChangeRef.current(key, sRGBHex);
      } catch {
        // kullanıcı iptal etti
      }
      return;
    }

    // --- Fallback: DOM pick modu ---
    setPickMode({ active: true, key });
  };

  // DOM pick modu (EyeDropper yoksa)
  useEffect(() => {
    if (!pickMode.active) return;

    document.body.classList.add("color-pick-mode");

    const pick = (e) => {
      // Editörün içine tıklanırsa yoksay
      if (editorRef.current?.contains(e.target)) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      const hex = pickColorFromElement(e.target);
      if (hex) onColorChangeRef.current(pickMode.key, hex);

      setPickMode({ active: false, key: null });
    };

    const cancel = (e) => {
      if (e.key === "Escape") setPickMode({ active: false, key: null });
    };

    document.addEventListener("click", pick, true);
    document.addEventListener("keydown", cancel);

    return () => {
      document.body.classList.remove("color-pick-mode");
      document.removeEventListener("click", pick, true);
      document.removeEventListener("keydown", cancel);
    };
  }, [pickMode.active, pickMode.key]);

  const handleBgFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setBgUploading(true);
    try {
      await onBgImageUpload(file);
    } finally {
      setBgUploading(false);
    }
  };

  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setLogoUploading(true);
    try {
      await onLogoImageUpload(file);
    } finally {
      setLogoUploading(false);
    }
  };

  const handleMouseDown = (e) => {
    if (e.target.closest(".editor-header")) {
      setIsDragging(true);
      const rect = editorRef.current.getBoundingClientRect();
      setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      const maxX = window.innerWidth - editorRef.current.offsetWidth;
      const maxY = window.innerHeight - editorRef.current.offsetHeight;
      setPosition({
        x: Math.min(Math.max(0, newX), maxX),
        y: Math.min(Math.max(0, newY), maxY),
      });
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  const handleReset = (key) => {
    // Hem renk hem alpha sıfırlanır → CSS default kullanılır
    onColorChange(key, null);
    onColorChange(`${key}Alpha`, null);
  };

  const row = (label, key, fallback) => {
    // Arama filtresi — label veya key arama terimini içermiyorsa null döner
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      if (!label.toLowerCase().includes(q) && !key.toLowerCase().includes(q)) return null;
    }
    return (
      <ColorRow
        key={key}
        label={label}
        colorKey={key}
        value={colors[key] || fallback || "#000000"}
        alphaValue={colors[`${key}Alpha`]}
        isPickActive={pickMode.active && pickMode.key === key}
        onPencilClick={handlePencilClick}
        onColorChange={onColorChange}
        onReset={handleReset}
      />
    );
  };

  // Tab boyutlandırma — search aktif değilse activeTab geçerli; search varken hepsi açık
  const inTab = (t) => !!searchTerm || activeTab === t;

  return (
    <div
      ref={editorRef}
      className={`css-editor design-editor-panel ${isDragging ? "dragging" : ""}`}
      style={{ position: "fixed", left: position.x, top: position.y, cursor: isDragging ? "grabbing" : "auto" }}
      onMouseDown={handleMouseDown}
    >
      <div className="editor-header" style={{ cursor: "grab" }}>
        <h3>Tasarım Editörü</h3>
        <button className="close-editor" onClick={onClose}>×</button>
      </div>

      {/* Arama kutusu */}
      <div className="de-search-row">
        <svg className="de-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
        <input
          type="text"
          className="de-search-input"
          placeholder="Ara: arkaplan, chip, modal..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onMouseDown={(e) => e.stopPropagation()}
        />
        {searchTerm && (
          <button className="de-search-clear" onClick={() => setSearchTerm('')} aria-label="Temizle">×</button>
        )}
      </div>

      {/* Tab bar (search aktifken devre dışı) */}
      <div className={`de-tabs ${searchTerm ? 'de-tabs--disabled' : ''}`}>
        {[
          { id: 'images',  label: 'Görseller' },
          { id: 'fonts',   label: 'Yazı Tipi' },
          { id: 'header',  label: 'Header' },
          { id: 'modals',  label: 'Modallar' },
          { id: 'general', label: 'Genel' },
        ].map(t => (
          <button
            key={t.id}
            type="button"
            className={`de-tab ${activeTab === t.id ? 'de-tab--active' : ''}`}
            onClick={() => setActiveTab(t.id)}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {t.label}
          </button>
        ))}
      </div>

      {pickMode.active && (
        <div className="pick-hint">
          <span>Sayfada bir renge tıkla</span>
          <kbd>Esc</kbd>
          <span>iptal</span>
        </div>
      )}

      {inTab('images') && (<>
      <div className="editor-section bg-image-section">
        <h4>Arkaplan Görseli</h4>

        <div className="bg-preview-box">
          {customBgImage ? (
            <img src={customBgImage} alt="Arkaplan" className="bg-preview-img" />
          ) : (
            <div className="bg-preview-empty">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="3"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <path d="M21 15l-5-5L5 21"/>
              </svg>
              <span>Henüz görsel yüklenmedi</span>
            </div>
          )}
        </div>

        <div className="bg-controls-row">
          <input
            ref={bgFileInputRef}
            type="file"
            accept="image/*"
            onChange={handleBgFileChange}
            style={{ display: 'none' }}
          />
          <button
            className={`bg-upload-btn${bgUploading ? ' loading' : ''}`}
            onClick={() => bgFileInputRef.current?.click()}
            disabled={bgUploading}
          >
            {bgUploading ? (
              <><span className="bg-spinner" /> Yükleniyor...</>
            ) : (
              <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg> Görsel Seç</>
            )}
          </button>
          {customBgImage && (
            <button className="bg-remove-btn" onClick={onBgImageRemove} disabled={bgUploading}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/></svg>
              Kaldır
            </button>
          )}
        </div>

        <label className="bg-toggle-row" style={{ pointerEvents: bgUploading ? 'none' : 'auto' }}>
          <div className={`bg-toggle-switch${colors.customBgEnabled ? ' on' : ''}`}
            onClick={() => onColorChange('customBgEnabled', !colors.customBgEnabled)}
          >
            <div className="bg-toggle-knob" />
          </div>
          <span className="bg-toggle-label">
            {colors.customBgEnabled ? 'Özel Arkaplan Aktif' : 'Özel Arkaplan Devre Dışı'}
          </span>
        </label>
      </div>

      <div className="editor-section bg-image-section">
        <h4>Logo Görseli</h4>

        <div className="bg-preview-box bg-preview-box--logo">
          {customLogoImage ? (
            <img src={customLogoImage} alt="Logo" className="bg-preview-img bg-preview-img--logo" />
          ) : (
            <div className="bg-preview-empty">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="3"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <path d="M21 15l-5-5L5 21"/>
              </svg>
              <span>Henüz logo yüklenmedi</span>
            </div>
          )}
        </div>

        <div className="bg-controls-row">
          <input
            ref={logoFileInputRef}
            type="file"
            accept="image/*"
            onChange={handleLogoFileChange}
            style={{ display: 'none' }}
          />
          <button
            className={`bg-upload-btn${logoUploading ? ' loading' : ''}`}
            onClick={() => logoFileInputRef.current?.click()}
            disabled={logoUploading}
          >
            {logoUploading ? (
              <><span className="bg-spinner" /> Yükleniyor...</>
            ) : (
              <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg> Logo Seç</>
            )}
          </button>
          {customLogoImage && (
            <button className="bg-remove-btn" onClick={onLogoImageRemove} disabled={logoUploading}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/></svg>
              Kaldır
            </button>
          )}
        </div>

        <label className="bg-toggle-row" style={{ pointerEvents: logoUploading ? 'none' : 'auto' }}>
          <div className={`bg-toggle-switch${colors.customLogoEnabled ? ' on' : ''}`}
            onClick={() => onColorChange('customLogoEnabled', !colors.customLogoEnabled)}
          >
            <div className="bg-toggle-knob" />
          </div>
          <span className="bg-toggle-label">
            {colors.customLogoEnabled ? 'Özel Logo Aktif' : 'Özel Logo Devre Dışı'}
          </span>
        </label>

        {/* Logo genişlik + margin sürgüleri */}
        <div className="logo-margin-controls">
          <div className="color-input">
            <label>
              Genişlik: {(colors.logoWidth || 0) === 0 ? 'Otomatik' : `${colors.logoWidth}px`}
            </label>
            <div className="slider-container">
              <input
                type="range"
                min="0"
                max="400"
                step="2"
                value={colors.logoWidth || 0}
                onChange={(e) => onColorChange('logoWidth', parseInt(e.target.value, 10))}
                className="opacity-slider"
              />
              <span className="color-value">
                {(colors.logoWidth || 0) === 0 ? 'auto' : `${colors.logoWidth}px`}
              </span>
            </div>
          </div>
          <div className="color-input">
            <label>
              Yükseklik: {(colors.logoHeight || 0) === 0 ? 'Otomatik' : `${colors.logoHeight}px`}
            </label>
            <div className="slider-container">
              <input
                type="range"
                min="0"
                max="200"
                step="2"
                value={colors.logoHeight || 0}
                onChange={(e) => onColorChange('logoHeight', parseInt(e.target.value, 10))}
                className="opacity-slider"
              />
              <span className="color-value">
                {(colors.logoHeight || 0) === 0 ? 'auto' : `${colors.logoHeight}px`}
              </span>
            </div>
          </div>
          <div className="color-input">
            <label>Üst Margin: {colors.logoMarginTop || 0}px</label>
            <div className="slider-container">
              <input
                type="range"
                min="-40"
                max="80"
                step="1"
                value={colors.logoMarginTop || 0}
                onChange={(e) => onColorChange('logoMarginTop', parseInt(e.target.value, 10))}
                className="opacity-slider"
              />
              <span className="color-value">{colors.logoMarginTop || 0}px</span>
            </div>
          </div>
          <div className="color-input">
            <label>Alt Margin: {colors.logoMarginBottom || 0}px</label>
            <div className="slider-container">
              <input
                type="range"
                min="-40"
                max="80"
                step="1"
                value={colors.logoMarginBottom || 0}
                onChange={(e) => onColorChange('logoMarginBottom', parseInt(e.target.value, 10))}
                className="opacity-slider"
              />
              <span className="color-value">{colors.logoMarginBottom || 0}px</span>
            </div>
          </div>
          <div className="color-input">
            <label>Sol Margin: {colors.logoMarginLeft || 0}px</label>
            <div className="slider-container">
              <input
                type="range"
                min="-80"
                max="80"
                step="1"
                value={colors.logoMarginLeft || 0}
                onChange={(e) => onColorChange('logoMarginLeft', parseInt(e.target.value, 10))}
                className="opacity-slider"
              />
              <span className="color-value">{colors.logoMarginLeft || 0}px</span>
            </div>
          </div>
          <div className="color-input">
            <label>Sağ Margin: {colors.logoMarginRight || 0}px</label>
            <div className="slider-container">
              <input
                type="range"
                min="-80"
                max="80"
                step="1"
                value={colors.logoMarginRight || 0}
                onChange={(e) => onColorChange('logoMarginRight', parseInt(e.target.value, 10))}
                className="opacity-slider"
              />
              <span className="color-value">{colors.logoMarginRight || 0}px</span>
            </div>
          </div>
          <div className="color-input">
            <label>Üst Padding: {colors.logoPaddingTop || 0}px</label>
            <div className="slider-container">
              <input
                type="range"
                min="0"
                max="60"
                step="1"
                value={colors.logoPaddingTop || 0}
                onChange={(e) => onColorChange('logoPaddingTop', parseInt(e.target.value, 10))}
                className="opacity-slider"
              />
              <span className="color-value">{colors.logoPaddingTop || 0}px</span>
            </div>
          </div>
          <div className="color-input">
            <label>Alt Padding: {colors.logoPaddingBottom || 0}px</label>
            <div className="slider-container">
              <input
                type="range"
                min="0"
                max="60"
                step="1"
                value={colors.logoPaddingBottom || 0}
                onChange={(e) => onColorChange('logoPaddingBottom', parseInt(e.target.value, 10))}
                className="opacity-slider"
              />
              <span className="color-value">{colors.logoPaddingBottom || 0}px</span>
            </div>
          </div>
          <div className="color-input">
            <label>Sol Padding: {colors.logoPaddingLeft || 0}px</label>
            <div className="slider-container">
              <input
                type="range"
                min="0"
                max="60"
                step="1"
                value={colors.logoPaddingLeft || 0}
                onChange={(e) => onColorChange('logoPaddingLeft', parseInt(e.target.value, 10))}
                className="opacity-slider"
              />
              <span className="color-value">{colors.logoPaddingLeft || 0}px</span>
            </div>
          </div>
          <div className="color-input">
            <label>Sağ Padding: {colors.logoPaddingRight || 0}px</label>
            <div className="slider-container">
              <input
                type="range"
                min="0"
                max="60"
                step="1"
                value={colors.logoPaddingRight || 0}
                onChange={(e) => onColorChange('logoPaddingRight', parseInt(e.target.value, 10))}
                className="opacity-slider"
              />
              <span className="color-value">{colors.logoPaddingRight || 0}px</span>
            </div>
          </div>
        </div>
      </div>

      </>)}

      {/* ── Header Detayları — granular renkler ── */}
      {inTab('header') && (<>
      <div className="editor-section">
        <h4>Welcome Kart (Hoşgeldiniz)</h4>
        {row("Arkaplan:", "welcomeBg", "#FFFFFF")}
        {row("Kenar:", "welcomeBorder", "#FFFFFF")}
        {row("Hover Kenar:", "welcomeBorderHover", "#FFFFFF")}
      </div>

      <div className="editor-section">
        <h4>Avatar</h4>
        {row("Kenar:", "avatarBorder", "#38BDF8")}
        {row("Glow:", "avatarGlow", "#38BDF8")}
        {row("Arkaplan:", "avatarBgColor", "#38BDF8")}
      </div>

      <div className="editor-section">
        <h4>Hoşgeldiniz Yazıları</h4>
        {row("'Hoşgeldiniz' Yazısı:", "greetingColor", "#FFFFFF")}
        {row("Kullanıcı Adı:", "usernameColor", "#38BDF8")}
        {row("Alt Yazı:", "subtitleColor", "#FFFFFF")}
      </div>

      <div className="editor-section">
        <h4>Hak Chip (N hak rozeti)</h4>
        {row("Arkaplan:", "chipBg", "#38BDF8")}
        {row("Kenar:", "chipBorder", "#38BDF8")}
        {row("Yazı:", "chipText", "#38BDF8")}
      </div>

      <div className="editor-section">
        <h4>Aksiyon Butonları (Hak Talep / Geçmiş)</h4>
        {row("Arkaplan:", "actionBtnBg", "#FFFFFF")}
        {row("Kenar:", "actionBtnBorder", "#FFFFFF")}
        {row("Yazı:", "actionBtnText", "#FFFFFF")}
      </div>

      <div className="editor-section">
        <h4>Aksiyon Butonları (Hover)</h4>
        {row("Hover Arkaplan:", "actionBtnHoverBg", "#FFFFFF")}
        {row("Hover Kenar:", "actionBtnHoverBorder", "#FFFFFF")}
        {row("Hover Yazı:", "actionBtnHoverText", "#FFFFFF")}
      </div>
      </>)}

      {inTab('fonts') && (<>
      <div className="editor-section">
        <h4>Yazı Tipleri (Türkçe Uyumlu)</h4>
        <div className="color-input">
          <div className="color-row-main" style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '500', color: 'rgba(255, 255, 255, 0.78)' }}>Çevir Butonu Yazı Tipi</label>
            <select
              className="de-select"
              value={colors.spinButtonFont || "Poppins"}
              onChange={(e) => onColorChange("spinButtonFont", e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {fontOptions.map((font) => (
                <option key={font.value} value={font.value}>
                  {font.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="color-input">
          <div className="color-row-main" style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '4px' }}>
            <label style={{ fontSize: '12px', fontWeight: '500', color: 'rgba(255, 255, 255, 0.78)' }}>Çark Dilimleri Yazı Tipi</label>
            <select
              className="de-select"
              value={colors.wheelSlicesFont || "Montserrat"}
              onChange={(e) => onColorChange("wheelSlicesFont", e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {fontOptions.map((font) => (
                <option key={font.value} value={font.value}>
                  {font.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="color-input" style={{ marginTop: '12px' }}>
          <label>Çark Dilimleri Yazı Boyutu: {colors.wheelSlicesFontSize || 20}px</label>
          <div className="slider-container">
            <input type="range" min="10" max="40" step="1"
              value={colors.wheelSlicesFontSize || 20}
              onChange={(e) => onColorChange('wheelSlicesFontSize', parseInt(e.target.value, 10))}
              className="opacity-slider"
            />
            <span className="color-value">{colors.wheelSlicesFontSize || 20}px</span>
          </div>
        </div>
      </div>
      </>)}

      {inTab('general') && (<>
      <div className="editor-section">
        <h4>Genel Tasarım</h4>
        {row("Arkaplan Gradient 1:", "backgroundGradient1")}
        {row("Arkaplan Gradient 2:", "backgroundGradient2")}
      </div>

      <div className="editor-section">
        <h4>Header Tasarımı</h4>
        {row("Header Arkaplan:", "headerBg")}
        {row("Header Item Arkaplan:", "headerItemBg")}
        {row("Header Kenar:", "headerBorderColor")}
        {row("İkon Rengi:", "headerIconColor")}
      </div>

      <div className="editor-section">
        <h4>Buton Tasarımı</h4>
        {row("Gradient 1:", "buttonGradient1")}
        {row("Gradient 2:", "buttonGradient2")}
        {row("Yazı Rengi:", "buttonTextColor")}
        {row("Kenar Rengi:", "buttonBorderColor")}
        {row("Parıltı Rengi:", "buttonGlowColor")}
      </div>
      </>)}

      {inTab('modals') && (<>
      <div className="editor-section">
        <h4>Tebrikler Modalı (PrizeModal)</h4>
        {row("Overlay Arkaplan:", "modalOverlayBg", "#000000")}
        {row("Modal Arkaplan:", "modalBg", "#1A2234")}
        {row("Modal Kenar:", "modalBorderColor", "#09F984")}
        {row("Başlık Rengi:", "modalHeaderColor", "#FFFFFF")}
        {row("Metin Rengi (Alt Yazı):", "modalTextColor", "#FFFFFF")}
        {row("Tik Rozeti Arkaplan:", "checkBg", "#09F984")}
        {row("Tik Rozeti Kenar:", "checkBorder", "#09F984")}
        {row("Tik Çizgisi:", "checkTick", "#09F984")}
        {row("Ödül Kutusu Arkaplan:", "modalPrizeBg", "#FFFFFF")}
        {row("Ödül Kutusu Kenar:", "modalPrizeBorder", "#FFFFFF")}
        {row("Ödül Etiketi (KAZANDIĞIN ÖDÜL):", "modalPrizeLabel", "#FFFFFF")}
        {row("Ödül Değeri:", "modalPrizeValue", "#FFFFFF")}
        {row("Buton Gradient 1:", "modalButtonGradient1", "#09F984")}
        {row("Buton Gradient 2:", "modalButtonGradient2", "#06C668")}
        {row("Buton Yazı:", "modalButtonText", "#0B111C")}
        {row("Perde (Ana Renk):", "curtainColor", "#8a1717")}
        {row("Perde (Koyu Ton):", "curtainDark", "#4d0a0a")}
        {row("Perde Kornisi (Üst):", "curtainCornice", "#d6a82a")}
      </div>

      <div className="editor-section">
        <h4>Geçmiş Modalı (HistoryModal)</h4>
        {row("Overlay Arkaplan:", "historyOverlayBg", "#000000")}
        {row("Modal Arkaplan:", "historyModalBg", "#1A2234")}
        {row("Modal Kenar:", "historyModalBorder", "#09F984")}
        {row("Başlık Rengi:", "historyHeaderColor", "#FFFFFF")}
        {row("Kapat İkonu:", "historyCloseColor", "#FFFFFF")}
        {row("Tarih Rengi:", "historyDateColor", "#FFFFFF")}
        {row("Ödül Yazısı:", "historyInfoValue", "#FFFFFF")}
        {row("Sayaç Badge BG:", "historyBadgeBg", "#09F984")}
        {row("Sayaç Badge Yazı:", "historyBadgeText", "#09F984")}
        {row("Satır Hover BG:", "historyItemBg", "#FFFFFF")}
        {row("Satır Çizgi:", "historyItemBorder", "#09F984")}
      </div>

      <div className="editor-section">
        <h4>Spin Hakkı Yok Modal (NoSpinAlert)</h4>
        {row("Overlay Arkaplan:", "noSpinOverlayBg", "#000000")}
        {row("Modal Arkaplan:", "noSpinBg", "#0F0C12")}
        {row("Modal Kenar:", "noSpinBorder", "#EF4444")}
        {row("Başlık:", "noSpinTitle", "#FFFFFF")}
        {row("Açıklama:", "noSpinText", "#FFFFFF")}
        {row("X İkonu:", "noSpinX", "#EF4444")}
        {row("Talep Et Butonu BG:", "noSpinClaimBg", "#EF4444")}
        {row("Talep Et Butonu Yazı:", "noSpinClaimText", "#FFFFFF")}
        {row("Tamam Butonu BG:", "noSpinCancelBg", "#FFFFFF")}
        {row("Tamam Butonu Yazı:", "noSpinCancelText", "#FFFFFF")}
      </div>

      <div className="editor-section">
        <h4>CSS Sıfırla Modalı (ResetConfirmation)</h4>
        {row("Overlay Arkaplan:", "resetOverlayBg", "#000000")}
        {row("Modal Arkaplan:", "resetBg", "#0F0C12")}
        {row("Modal Kenar:", "resetBorder", "#09F984")}
        {row("Başlık:", "resetTitle", "#FFFFFF")}
        {row("Açıklama:", "resetText", "#FFFFFF")}
        {row("İptal Butonu BG:", "resetCancelBg", "#FFFFFF")}
        {row("İptal Butonu Yazı:", "resetCancelText", "#FFFFFF")}
        {row("Onayla Butonu BG:", "resetConfirmBg", "#EF4444")}
        {row("Onayla Butonu Yazı:", "resetConfirmText", "#FFFFFF")}
      </div>
      </>)}
    </div>
  );
};

export default DesignEditor;
